import React, { FunctionComponent, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import cn from 'classnames';

import { isActionKey } from '../../utils/keyboard';
import ImageGridItem from './ImageGridItem/ImageGridItem';
import ImageGridOverflowOverlay from './ImageGridOverflowOverlay/ImageGridOverflowOverlay';
import { Image } from './imageGridTypes';

import styles from './ImageGrid.scss';

type ImageGridType = 'template' | 'equal';

export interface ImageGridProps {
  images: Image[];
  gridType: ImageGridType;
  className?: string;
  onImageDelete?: (index: number) => void;
  onImageSelect?: (index: Image) => void;
  onImageError?: (image: Image) => void;
}

type ImageGridCssExports = typeof styles & { [key: string]: string };

export const ImageGrid: FunctionComponent<ImageGridProps> = ({
  images,
  gridType,
  className = '',
  onImageDelete,
  onImageSelect = () => {},
  onImageError,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });

  const { gridImages, rest } = chunkImages(images, gridType);
  const gridClass = getGridClass(gridImages, gridType);
  const maxGridSize = MAX_GRID_SIZES[gridType];
  const imagesInOverflow = rest.length;
  const isFlexibleSquare = gridType === 'equal';

  const onKeyDown = (event: KeyboardEvent, image: Image) => {
    if (isActionKey(event)) {
      onImageSelect(image);
      event.stopPropagation();
    }
  };

  return (
    <div
      aria-label={t('timeline.gallery')}
      className={cn(
        styles.grid,
        gridClass,
        gridType === 'template' && styles.fixedGridHeight,
        className,
      )}
    >
      {gridImages.map((image, index) => {
        const hideButton = index + 1 === maxGridSize && imagesInOverflow > 0;

        return (
          <ImageGridItem
            key={image.id}
            image={image}
            isFlexibleSquare={isFlexibleSquare}
            hideButton={hideButton}
            className={cn(
              (styles as ImageGridCssExports)[`gridItem${index + 1}`],
            )}
            onDelete={onImageDelete ? () => onImageDelete(index) : undefined}
            onKeyDown={event => onKeyDown(event, image)}
            onClick={() => onImageSelect(image)}
            onError={onImageError}
          />
        );
      })}
      <ImageGridOverflowOverlay
        className={styles.imageOverflowBlockPosition}
        imagesInOverflow={imagesInOverflow}
      />
    </div>
  );
};

export default ImageGrid;

const MAX_GRID_SIZES: Record<ImageGridType, number> = {
  template: 5,
  equal: 6,
};

function chunkImages(images: Image[], gridType: ImageGridType) {
  const MAX_GRID_SIZE = MAX_GRID_SIZES[gridType];
  let gridImages: typeof images = [];
  let rest: typeof images = [];

  if (images.length > MAX_GRID_SIZE) {
    gridImages = images.filter((_, index) => index < MAX_GRID_SIZE);
    rest = images.filter((_, index) => index >= MAX_GRID_SIZE);
  } else {
    gridImages = images;
  }

  return { gridImages, rest };
}

function getGridClass(images: Image[], gridType: ImageGridType) {
  return gridType === 'template'
    ? (styles as ImageGridCssExports)[`imageGrid${images.length}`]
    : styles.imageGridEqual;
}
